import { useEffect, useMemo, useState } from 'react';
import TextWinds from '../../../common/TextWinds';
import {
  AssignmentCategory,
  StudentProgress,
} from '../../../../constants/types/lms/student/homework/type';
import BookProgressCard from './BookProgressCard';

interface StudentProgressMapPanelProps {
  selectedCategory: AssignmentCategory;
  progress: StudentProgress | null;
}

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬 > 우측 젭스온(AI인증제 제외), WMW, HFHJ [진도 지도]
 */
const StudentProgressMapPanel = ({
  selectedCategory,
  progress,
}: StudentProgressMapPanelProps) => {
  const filteredProgress = useMemo(() => {
    switch (selectedCategory) {
      case 'JEBSON':
        const jebsonBooks = progress.books
          .filter((book) =>
            book.subjects.some((subject) =>
              ['EC', 'EF', 'ESB'].includes(subject.subjectCode),
            ),
          )
          .map((book, index) => ({
            ...book,
            id: `${book.book}-${index}`,
          }));

        return {
          ...progress,
          books: jebsonBooks,
        };

      default:
        const books = progress.books.map((book, index) => ({
          ...book,
          id: `${book.book}-${index}`,
        }));

        return {
          ...progress,
          books,
        };
    }
  }, [selectedCategory, progress]);

  const [openedBookIds, setOpenedBookIds] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!filteredProgress) return;

    const initiallyOpenedBookIds = filteredProgress.books.map(
      (book) => book.id,
    );

    setOpenedBookIds(new Set(initiallyOpenedBookIds));
  }, [filteredProgress]);

  if (!filteredProgress) {
    return (
      <div className="flex h-[320px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
        <TextWinds type="content_body2" color="gray6">
          진도 데이터가 없습니다.
        </TextWinds>
      </div>
    );
  }

  const handleBookOpenToggle = (bookId: string) => {
    setOpenedBookIds((prev) => {
      const next = new Set(prev);

      if (next.has(bookId)) {
        next.delete(bookId);
      } else {
        next.add(bookId);
      }

      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <ProgressLegend
          colorClassName="border-green-200 bg-green-100"
          label="완료"
        />
        <ProgressLegend
          colorClassName="border-blue-200 bg-blue-100"
          label="진행 중"
        />
      </section>

      {filteredProgress.books.length === 0 ? (
        <div className="flex h-[260px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
          <TextWinds type="content_body2" color="gray6">
            진도 지도 데이터가 없습니다.
          </TextWinds>
        </div>
      ) : (
        <section className="flex flex-col gap-3">
          {filteredProgress.books.map((book) => {
            const isOpened = openedBookIds.has(book.id);

            return (
              <BookProgressCard
                key={book.id}
                selectedCategory={selectedCategory}
                book={book}
                isOpened={isOpened}
                onOpenToggle={() => handleBookOpenToggle(book.id)}
              />
            );
          })}
        </section>
      )}
    </div>
  );
};

export default StudentProgressMapPanel;

interface ProgressLegendProps {
  colorClassName: string;
  label: string;
}

const ProgressLegend = ({ colorClassName, label }: ProgressLegendProps) => {
  return (
    <div className="flex items-center gap-1.5">
      <span className={['h-4 w-4 rounded border', colorClassName].join(' ')} />

      <TextWinds type="caption1" color="gray7">
        {label}
      </TextWinds>
    </div>
  );
};
